import { useState, useEffect, useCallback, useMemo } from 'react';
import { Polyline, CircleMarker, Popup, useMapEvents } from 'react-leaflet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Scissors, Droplets, AlertTriangle, Truck, Mountain,
  Package, TreePine, Snowflake, Waves, Wrench, Settings, Route,
  Eye, EyeOff, Ruler, Receipt, MapPin, X, Save, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs par type d'intervention
const INTERVENTION_COLORS = {
  epareuse: '#22c55e',
  debroussaillage: '#16a34a',
  curage_fosse: '#3b82f6',
  fuite: '#ef4444',
  refection_chemin: '#f59e0b',
  gravier: '#a3a3a3',
  depot: '#8b5cf6',
  bennage_terre: '#92400e',
  elagage: '#065f46',
  deneigement: '#93c5fd',
  curage_mare: '#0ea5e9',
  debouchage: '#6366f1',
  irrigation: '#06b6d4',
  tranchee: '#d97706',
  drainage: '#0d9488',
  autre: '#737373',
};

const INTERVENTION_LABELS = {
  epareuse: 'Epareuse',
  debroussaillage: 'Debroussaillage',
  curage_fosse: 'Curage fosse',
  fuite: 'Reparation fuite',
  refection_chemin: 'Refection chemin',
  gravier: 'Gravier',
  depot: 'Depot',
  bennage_terre: 'Bennage terre',
  elagage: 'Elagage',
  deneigement: 'Deneigement',
  curage_mare: 'Curage mare',
  debouchage: 'Debouchage',
  irrigation: 'Reseau irrigation',
  tranchee: 'Tranchee',
  drainage: 'Drainage',
  autre: 'Autre',
};

// Calculate distance between 2 GPS points (Haversine)
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function totalDistance(points) {
  let d = 0;
  for (let i = 1; i < points.length; i++) {
    d += haversineDistance(points[i - 1].lat, points[i - 1].lng, points[i].lat, points[i].lng);
  }
  return d;
}

export default function TracesTravaux({ token, societeId, visible, onToggle }) {
  const [traces, setTraces] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [drawMode, setDrawMode] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [currentType, setCurrentType] = useState('epareuse');
  const [showNewTrace, setShowNewTrace] = useState(false);
  const [newTraceData, setNewTraceData] = useState({ nom: '', client_nom: '', commune: '', type_trace: 'route', mode_facturation: 'heure', tarif_unitaire: 85 });
  const [selectedTrace, setSelectedTrace] = useState(null);

  const headers = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    if (societeId) h['X-Societe-Id'] = societeId;
    return h;
  }, [token, societeId]);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, iRes] = await Promise.all([
        fetch(`${API_URL}/api/eta-travaux/traces`, { headers }),
        fetch(`${API_URL}/api/eta-travaux/interventions`, { headers }),
      ]);
      if (tRes.ok) { const d = await tRes.json(); setTraces(d.traces || []); }
      if (iRes.ok) { const d = await iRes.json(); setInterventions(d.interventions || []); }
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { if (visible) fetchData(); }, [visible, fetchData]);

  // Map click handler for drawing mode
  function MapClickHandler() {
    useMapEvents({
      click(e) {
        if (drawMode) {
          setCurrentPoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
        }
      }
    });
    return null;
  }

  const saveTrace = async () => {
    if (currentPoints.length < 2) { toast.error('Placez au moins 2 points'); return; }
    const dist = totalDistance(currentPoints);
    try {
      const res = await fetch(`${API_URL}/api/eta-travaux/traces`, {
        method: 'POST', headers,
        body: JSON.stringify({
          ...newTraceData,
          longueur_m: Math.round(dist),
          points_gps: currentPoints,
        })
      });
      if (res.ok) {
        toast.success(`Trace sauvegarde (${Math.round(dist)}m)`);
        setDrawMode(false);
        setCurrentPoints([]);
        setShowNewTrace(false);
        fetchData();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  if (!visible) return <MapClickHandler />;

  const traceLines = traces.filter(t => t.points_gps?.length >= 2);

  return (
    <>
      <MapClickHandler />

      {/* Traces existants = polylines */}
      {traceLines.map(trace => {
        const positions = trace.points_gps.map(p => [p.lat, p.lng]);
        // Get the dominant intervention type for color
        const traceInterventions = interventions.filter(i => i.trace_id === trace.id);
        const dominantType = traceInterventions.length > 0
          ? traceInterventions.reduce((acc, i) => { acc[i.type_intervention] = (acc[i.type_intervention] || 0) + 1; return acc; }, {})
          : {};
        const topType = Object.keys(dominantType).sort((a, b) => dominantType[b] - dominantType[a])[0] || 'autre';
        const color = INTERVENTION_COLORS[topType] || '#737373';

        return (
          <Polyline
            key={trace.id}
            positions={positions}
            pathOptions={{ color, weight: 4, opacity: 0.8, dashArray: trace.type_trace === 'fosse' ? '10 5' : undefined }}
            eventHandlers={{ click: () => setSelectedTrace(trace) }}
          >
            <Popup>
              <div className="text-xs min-w-[200px]">
                <p className="font-bold text-sm">{trace.nom}</p>
                <p className="text-gray-500">{trace.client_nom} — {trace.commune}</p>
                <p>{trace.longueur_m}m — {trace.mode_facturation} ({trace.tarif_unitaire} EUR)</p>
                <p>{trace.nb_interventions} interventions</p>
              </div>
            </Popup>
          </Polyline>
        );
      })}

      {/* Points GPS de chaque trace */}
      {traceLines.map(trace =>
        trace.points_gps.map((pt, idx) => {
          const traceIntervs = interventions.filter(i => i.trace_id === trace.id);
          const topType = traceIntervs.length > 0 ? traceIntervs[0].type_intervention : 'autre';
          return (
            <CircleMarker
              key={`${trace.id}-pt-${idx}`}
              center={[pt.lat, pt.lng]}
              radius={6}
              pathOptions={{
                color: INTERVENTION_COLORS[topType] || '#737373',
                fillColor: INTERVENTION_COLORS[topType] || '#737373',
                fillOpacity: 0.9,
                weight: 2,
              }}
            >
              <Popup>
                <div className="text-xs">
                  <p className="font-bold">{trace.nom} — Point {idx + 1}</p>
                  {idx > 0 && (
                    <p>Distance depuis pt precedent: {Math.round(haversineDistance(
                      trace.points_gps[idx - 1].lat, trace.points_gps[idx - 1].lng, pt.lat, pt.lng
                    ))}m</p>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          );
        })
      )}

      {/* Points de trace en cours de dessin */}
      {drawMode && currentPoints.map((pt, idx) => (
        <CircleMarker
          key={`draw-${idx}`}
          center={[pt.lat, pt.lng]}
          radius={8}
          pathOptions={{ color: INTERVENTION_COLORS[currentType], fillColor: INTERVENTION_COLORS[currentType], fillOpacity: 1, weight: 3 }}
        >
          <Popup><span className="text-xs">Point {idx + 1} — {idx > 0 ? `${Math.round(haversineDistance(currentPoints[idx - 1].lat, currentPoints[idx - 1].lng, pt.lat, pt.lng))}m` : 'Depart'}</span></Popup>
        </CircleMarker>
      ))}
      {drawMode && currentPoints.length >= 2 && (
        <Polyline
          positions={currentPoints.map(p => [p.lat, p.lng])}
          pathOptions={{ color: INTERVENTION_COLORS[currentType], weight: 4, opacity: 0.9, dashArray: '8 4' }}
        />
      )}

      {/* Panneau lateral flottant */}
      <div className="absolute right-2 top-2 z-[1000] w-64 space-y-2" data-testid="traces-panel">
        {/* Bouton toggle */}
        <div className="bg-[#0d1321]/95 backdrop-blur rounded-lg p-2 flex items-center justify-between">
          <span className="text-xs font-bold text-white flex items-center gap-1"><Route className="w-3.5 h-3.5" /> Travaux ETA</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => { setDrawMode(true); setShowNewTrace(true); }} className="h-6 w-6 p-0 text-emerald-400">
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Mode dessin */}
        {drawMode && (
          <div className="bg-[#0d1321]/95 backdrop-blur rounded-lg p-3 space-y-2 border border-emerald-500/30">
            <p className="text-xs text-emerald-400 font-bold">Cliquez sur la carte pour poser des points</p>
            <p className="text-xs text-slate-300">Points: {currentPoints.length} — Distance: {Math.round(totalDistance(currentPoints))}m</p>
            
            <select value={currentType} onChange={e => setCurrentType(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded p-1 text-xs text-white">
              {Object.entries(INTERVENTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>

            {showNewTrace && (
              <>
                <input value={newTraceData.nom} onChange={e => setNewTraceData({ ...newTraceData, nom: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded p-1 text-xs text-white" placeholder="Nom du trace" />
                <input value={newTraceData.client_nom} onChange={e => setNewTraceData({ ...newTraceData, client_nom: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded p-1 text-xs text-white" placeholder="Client (ex: Mairie de...)" />
                <input value={newTraceData.commune} onChange={e => setNewTraceData({ ...newTraceData, commune: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded p-1 text-xs text-white" placeholder="Commune" />
                <div className="flex gap-1">
                  <select value={newTraceData.mode_facturation} onChange={e => setNewTraceData({ ...newTraceData, mode_facturation: e.target.value })}
                    className="flex-1 bg-slate-900 border border-slate-700 rounded p-1 text-xs text-white">
                    <option value="heure">A l'heure</option>
                    <option value="metre">Au metre</option>
                    <option value="forfait">Forfait</option>
                  </select>
                  <input type="number" value={newTraceData.tarif_unitaire} onChange={e => setNewTraceData({ ...newTraceData, tarif_unitaire: parseFloat(e.target.value) || 0 })}
                    className="w-16 bg-slate-900 border border-slate-700 rounded p-1 text-xs text-white" placeholder="EUR" />
                </div>
              </>
            )}

            <div className="flex gap-1">
              <Button size="sm" onClick={saveTrace} className="bg-emerald-600 hover:bg-emerald-700 text-xs flex-1" disabled={currentPoints.length < 2}>
                <Save className="w-3 h-3 mr-1" /> Sauvegarder
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setDrawMode(false); setCurrentPoints([]); setShowNewTrace(false); }}
                className="border-slate-600 text-xs">
                <X className="w-3 h-3" />
              </Button>
            </div>
            {currentPoints.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setCurrentPoints(prev => prev.slice(0, -1))} className="text-xs text-slate-400 w-full">
                Annuler dernier point
              </Button>
            )}
          </div>
        )}

        {/* Legende couleurs */}
        {!drawMode && traces.length > 0 && (
          <div className="bg-[#0d1321]/95 backdrop-blur rounded-lg p-2 space-y-1">
            <p className="text-[10px] text-slate-400 font-bold uppercase">Traces ({traces.length})</p>
            {traces.slice(0, 8).map(t => (
              <div key={t.id} className="flex items-center gap-1.5 cursor-pointer hover:bg-white/5 rounded px-1 py-0.5"
                onClick={() => setSelectedTrace(t)}>
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: INTERVENTION_COLORS[interventions.find(i => i.trace_id === t.id)?.type_intervention] || '#737373' }} />
                <span className="text-[10px] text-white truncate">{t.nom}</span>
                <span className="text-[9px] text-slate-500 ml-auto">{t.longueur_m}m</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail trace modal */}
      <Dialog open={!!selectedTrace} onOpenChange={() => setSelectedTrace(null)}>
        <DialogContent className="max-w-lg bg-[#0d1321] border-slate-700 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">{selectedTrace?.nom}</DialogTitle>
          </DialogHeader>
          {selectedTrace && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-400">Client:</span> <span className="text-white">{selectedTrace.client_nom}</span></div>
                <div><span className="text-slate-400">Commune:</span> <span className="text-white">{selectedTrace.commune}</span></div>
                <div><span className="text-slate-400">Longueur:</span> <span className="text-white">{selectedTrace.longueur_m}m</span></div>
                <div><span className="text-slate-400">Facturation:</span> <span className="text-white">{selectedTrace.mode_facturation} ({selectedTrace.tarif_unitaire} EUR)</span></div>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-300 mb-1">Interventions ({interventions.filter(i => i.trace_id === selectedTrace.id).length})</p>
                {interventions.filter(i => i.trace_id === selectedTrace.id).map(i => (
                  <div key={i.id} className="text-xs bg-slate-800/50 rounded p-2 mb-1 border-l-2" style={{ borderColor: INTERVENTION_COLORS[i.type_intervention] || '#737373' }}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{INTERVENTION_LABELS[i.type_intervention] || i.type_intervention}</span>
                      <span className="text-slate-400">{i.date_debut}</span>
                    </div>
                    {i.point_depart && <p className="text-slate-400 mt-0.5">{i.point_depart} → {i.point_arrivee}</p>}
                    <div className="flex gap-3 text-slate-400 mt-0.5">
                      {i.duree_heures && <span>{i.duree_heures}h</span>}
                      {i.longueur_faite_m && <span>{i.longueur_faite_m}m</span>}
                      {i.nb_passes > 1 && <span>{i.nb_passes} passes</span>}
                      {i.montant_calcule && <span className="text-emerald-400">{i.montant_calcule} EUR</span>}
                    </div>
                    {i.operateur && <p className="text-slate-500 mt-0.5">{i.operateur} — {i.materiel}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
